import React, { useEffect, useState } from 'react';
import { Box } from 'lmc-ui-library';
import css from './Documents.module.scss';
import { Button } from 'modules/af_cm_ui_kit';
import { Props } from './Documents.types';
import GenerateDocumentsErrorModal from 'modules/cm/pages/CaseDetailsPage/partials/GenerateDocumentsErrorModal/GenerateDocumentsErrorModal';
import { caseApi } from 'modules/cm/api';
import { GenerateOfferPackError } from 'modules/cm/api/cases/case/generateOfferPack';
import { refreshCase, saveCase } from 'modules/cm/store/cases/actions';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from 'form/components/Select/Select.styled';
import { getIsCaseDirty } from '../../../../store/subPages/selectors';
import useSaveButtonClicked from '../../hooks/useSaveButtonClicked';
import { generatePreOfferESISError } from 'modules/cm/api/cases/case/generatePreOfferESIS';

const Documents: React.FC<Props> = ({ caseId, offerPackUrl, preOfferEsisUrl }) => {
  const dispatch = useDispatch();
  const [showOfferPackErrorsModal, setShowOfferPackErrorsModal] = useState<boolean>(false);
  const [showPreOfferESISErrorsModal, setShowPreOfferESISErrorsModal] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [offerPackErrors, setOfferPackErrors] = useState<string[]>([]);
  const [preOfferESISErrors, setPreOfferESISErrors] = useState<string[]>([]);
  const [isGenerateOfferPackBtnClicked, setIsGenerateOfferPackBtnClicked] = useState<boolean>(false);
  const [isGeneratePreOfferESISBtnClicked, setIsGeneratePreOfferESISBtnClicked] = useState<boolean>(false);
  const isDirty = useSelector(getIsCaseDirty);
  const { addListener, removeListener } = useSaveButtonClicked();
  useEffect(() => {
    const listener = addListener(() => {
      dispatch(saveCase());
    });
    return () => {
      removeListener(listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerateOfferPack = async () => {
    if (isRefreshing) {
      return;
    }
    setIsGenerateOfferPackBtnClicked(true);
    setIsRefreshing(true);
    try {
      await caseApi(caseId).generateOfferPack();
      await dispatch(refreshCase(caseId));
    } catch (e) {
      if (e instanceof GenerateOfferPackError) {
        const errors = e.getErrors();
        setOfferPackErrors(Array.isArray(errors) && errors.length > 0 ? errors : ['Unexpected error']);
      } else {
        setOfferPackErrors(['Unexpected error']);
      }
      setShowOfferPackErrorsModal(true);
      setIsGenerateOfferPackBtnClicked(false);
    }
    setIsRefreshing(false);
    setIsGenerateOfferPackBtnClicked(false);
  };

  const handleGeneratePreOfferESIS = async () => {
    if (isRefreshing) {
      return;
    }
    setIsRefreshing(true);
    setIsGeneratePreOfferESISBtnClicked(true);
    try {
      await caseApi(caseId).generatePreOfferESIS();
      await dispatch(refreshCase(caseId));
    } catch (e) {
      if (e instanceof generatePreOfferESISError) {
        const errors = e.getErrors();
        setPreOfferESISErrors(Array.isArray(errors) && errors.length > 0 ? errors : ['Unexpected error']);
      } else {
        setPreOfferESISErrors(['Unexpected error']);
      }
      setShowPreOfferESISErrorsModal(true);
      setIsGeneratePreOfferESISBtnClicked(false);
    }
    setIsRefreshing(false);
    setIsGeneratePreOfferESISBtnClicked(false);
  };

  return (
    <>
      <Box className={css.documents}>
        <Box.Content>
          <div className={css.header}>
            <h1>Documents</h1>
          </div>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'space-between' }}>
            <Button mode="secondary" onClick={() => handleGenerateOfferPack()} disabled={isRefreshing || isDirty}>
              {isRefreshing && isGenerateOfferPackBtnClicked ? (
                <Loader style={{ margin: '0 auto' }} />
              ) : (
                'Generate Offer Pack'
              )}
            </Button>
            <a
              href={offerPackUrl && !isRefreshing ? offerPackUrl : '/#'}
              download={offerPackUrl && !isRefreshing ? `${caseId}-offer-pack.zip` : undefined}
              className={css.link}>
              <Button mode="secondary" disabled={!offerPackUrl || isRefreshing}>
                Download Offer Pack
              </Button>
            </a>
          </div>

          <div style={{ display: 'flex', gap: '15px', justifyContent: 'space-between', marginTop: '24px' }}>
            <Button mode="secondary" onClick={() => handleGeneratePreOfferESIS()} disabled={isRefreshing || isDirty}>
              {isRefreshing && isGeneratePreOfferESISBtnClicked ? (
                <Loader style={{ margin: '0 auto' }} />
              ) : (
                'Generate Pre-Offer ESIS'
              )}
            </Button>
            <a
              href={preOfferEsisUrl && !isRefreshing ? preOfferEsisUrl : '/#'}
              target="_blank"
              className={css.link}
              rel="noopener noreferrer">
              <Button mode="secondary" disabled={!preOfferEsisUrl || isRefreshing}>
                Download Pre-Offer ESIS
              </Button>
            </a>
          </div>
        </Box.Content>
      </Box>
      <GenerateDocumentsErrorModal
        errors={offerPackErrors}
        isOpen={showOfferPackErrorsModal}
        onClose={() => setShowOfferPackErrorsModal(false)}
      />
      <GenerateDocumentsErrorModal
        errors={preOfferESISErrors}
        isOpen={showPreOfferESISErrorsModal}
        onClose={() => setShowPreOfferESISErrorsModal(false)}
      />
    </>
  );
};

export default Documents;
